const MAX_INT = Number.MAX_SAFE_INTEGER;

export class Node {
  constructor(children, validation = {}, description = '') {
    this.children = children;
    this.validation = {
      min_children: 0,
      max_children: MAX_INT,
      min_credit: 0,
      min_valid_children_node: MAX_INT,
      ...validation,
    };
    this.description = description;

    this.credit = 0;
    this.valid = false;
    this.selected = false;
  }

  select() {
    this.selected = true;
  }

  unselect() {
    this.selected = false;
  }

  validate() {
    const {
      min_children,
      max_children,
      min_credit,
      min_valid_children_node,
    } = this.validation, { children } = this;

    children.forEach(v => v.validate());

    const selected_children = children
      .map((v, i) => i)
      .filter(v => children[v].valid && children[v].credit > 0)
      .sort((a, b) => children[b].credit - children[a].credit)
      .slice(0, max_children)
      .map(v => children[v]);
    let credit = 0;
    selected_children.forEach(c => {
      c.select();
      credit += c.credit;
    });
    this.credit = credit;

    const children_nodes = children.filter(v => v instanceof Node);
    const valid_children_node = children_nodes.filter(v => v.valid);
    this.valid =
      valid_children_node.length >= Math.min(min_valid_children_node, children_nodes.length) &&
      selected_children.length >= min_children &&
      credit >= min_credit;

    this.unselect();
  }
}

class Course {
  constructor({ name, records, credits, enrolled_course_indices }) {
    this.name = name;
    this.records = records;
    this.credits = credits;

    this.enrolled = enrolled_course_indices.length > 0;
    this.record = records[enrolled_course_indices[0]] || null;
    this.credit = credits[enrolled_course_indices[0]] || 0;

    this.validation = {
      related_courses: [],
      truth_table: [],
    }

    this.selectable = false;
    this.valid = false;
    this.selected = false;
  }

  relate(related_courses, truth_table) {
    this.validation.related_courses = related_courses;
    this.validation.truth_table = truth_table;
  }

  select() {
    this.selected = true;
  }

  unselect() {
    this.selected = false;
  }

  validate() {
    const { related_courses, truth_table } = this.validation;
    this.selectable = related_courses.length === 0 ? true : truth_table[related_courses.reverse().map(v => v.enrolled).reduce((acc, cur, idx) => acc + (cur << idx), 0)];
    this.valid = this.enrolled && this.selectable;
    this.unselect();
  }
}

export const PROGRAMS = [
  { code: 'EC', name: '電子商務' },
  { code: 'DS', name: '數據科學' },
  { code: 'IE', name: '創新創業' },
  { code: 'ID', name: '創新設計' },
  { code: 'BM', name: '生物科技管理' },
  { code: 'BTB', name: '生物產業技術' },
  { code: 'SDEM', name: '永續發展與環境管理' },
  { code: 'TT', name: '觀光旅遊' },
  { code: 'LAW', name: '法律' },
  { code: 'EF', name: '教育知能' },
  { code: 'ART', name: '藝術' },
  { code: 'ICM', name: '資訊傳媒' },
  
];

class Program {
  constructor(programs, enrolled_courses, test = false) {
    // enrolled_courses = ['10710KLST630300', '10710CS  390200', ...]
    this.courses = {};
    Object.keys(programs).forEach(p => {
      this.courses[p] = programs[p].map(v => new Course({
        ...v,
        enrolled_course_indices: enrolled_courses.map(c => v.records.indexOf(c)).filter(i => i !== -1),
      }));
    });

    this.test = test;
  }

  printEnrolled(courses) {
    console.log(courses.filter(v => v.enrolled).map(v => [v.name, v.credit]));
  }

  // 資訊傳媒學分學程
  ICM() {
    const courses = this.courses['資訊傳媒學分學程'];
    if (this.test) this.printEnrolled(courses);

    // 必修
    // 媒體與社會、媒體與寫作二擇一
    const sim1 = new Node(courses.slice(0, 2), { max_children: 1 }, '擇一');

    // 系統整合實作一二合算一門
    courses[2].relate([courses[3]], [false, true]);
    courses[3].relate([courses[2]], [false, true]);
    const and1 = new Node(courses.slice(2, 4), {}, '合算');

    // 任選1(至多2)
    const tbl1 = new Node([sim1, and1, courses[4]], {
      min_children: 1,
      max_children: 2,
    }, '專題課程');

    const cat1 = new Node([tbl1], {}, '一、必修：至少 1 門，至多 2 門課');

    // 選修
    // 程式設計入門、計算機程式設計、計算機程式設計一、計算機程式設計二，擇一
    const sim2 = new Node(courses.slice(18, 22), { max_children: 1 }, '擇一');

    // 選修
    const tbl2 = new Node([...courses.slice(5, 18), sim2, ...courses.slice(22)], {}, '選修');

    const cat2 = new Node([tbl2], {}, '二、選修');

    // 資訊傳媒學分學程
    const root = new Node([cat1, cat2], { min_credit: 15 });
    root.validate();
    if (this.test) console.log(root.validate(), root.credit);
    return root;
  }

  // 生物產業技術學分學程
  BTB() {
    const courses = this.courses['生物產業技術學分學程'];
    if (this.test) this.printEnrolled(courses);

    // 必修
    // 分子生物學一、分子與細胞生物學一、細胞生物學，擇一
    const sim1 = new Node(courses.slice(3, 6), { max_children: 1 }, '擇一');

    // 分子生物學二、分子與細胞生物學二、分子生物學，擇一
    const sim2 = new Node(courses.slice(6, 9), { max_children: 1 }, '擇一');

    // 若選擇「生物化學一」或「生物化學二」,則不可將醫科系「生物化學」列入，反之亦然
    courses[10].relate([courses[11], courses[12]], [true, false, true, true]);
    courses[11].relate([courses[10], courses[12]], [true, false, true, true]);
    courses[12].relate([courses[10], courses[11]], [true, true, true, false]);

    // 必修，9 選 3
    const tbl1 = new Node([...courses.slice(0, 3), sim1, sim2, ...courses.slice(9, 13)], { min_children: 3 }, '必修');

    const cat1 = new Node([tbl1], {}, '一、必修：至少 3 門課');

    //  選修
    // 蛋白體學、蛋白質體學原理與應用，擇一
    const sim3 = new Node(courses.slice(34, 36), { max_children: 1 }, '擇一');

    // 選修
    const tbl2 = new Node([...courses.slice(13, 34), sim3, ...courses.slice(36)], {}, '選修');

    const cat2 = new Node([tbl2], {}, '二、 選修');

    // 生物產業技術學分學程
    const root = new Node([cat1, cat2], { min_credit: 15 });
    root.validate();
    if (this.test) console.log(root.validate(), root.credit);
    return root;
  }

  // 電子商務學分學程
  EC() {
    const courses = this.courses['電子商務學分學程'];
    if (this.test) this.printEnrolled(courses);

    // 選修-A:管理類
    // 電子商務、數位創新與電子商務，擇一
    const sim1 = new Node(courses.slice(0, 2), { max_children: 1 }, '擇一');

    // 物流管理、全球運籌管理，擇一
    const sim2 = new Node(courses.slice(10, 12), { max_children: 1 }, '擇一');

    // 市場與行銷、行銷研究方法、晶片系統行銷管理、網路行銷、科技行銷、行銷管理，擇一
    const sim3 = new Node(courses.slice(12, 18), { max_children: 1 }, '擇一');

    // 選修-A:管理類，至少三學分
    const tbl1 = new Node([sim1, ...courses.slice(2, 10), sim2, sim3], { min_credit: 3 }, '管理類');

    const cat1 = new Node([tbl1], {}, '一、管理類：至少 3 學分');

    // 選修-B:技術類，至少三學分
    const tbl2 = new Node(courses.slice(18), { min_credit: 3 }, '技術類');

    const cat2 = new Node([tbl2], {}, '二、技術類：至少 3 學分');

    // 電子商務學分學程
    const root = new Node([cat1, cat2], { min_credit: 15 });
    root.validate();
    if (this.test) console.log(root.validate(), root.credit);
    return root;
  }

  // 生物科技管理學分學程
  BM() {
    const courses = this.courses['生物科技管理學分學程'];
    if (this.test) this.printEnrolled(courses);

    // 必修(至少9學分)
    // 生技產業導論、生技產業專題，擇一
    const sim1 = new Node(courses.slice(4, 6), { max_children: 1 }, '擇一');

    // 非生技背景學生必修（5選2）
    const tbl1 = new Node([...courses.slice(0, 4), sim1], { min_children: 2 }, '非生技背景學生必修：至少 2 門');

    // 非管理背景學生必修
    const tbl2 = new Node([courses[6]], { min_children: 1 }, '非管理背景學生必修');

    // 科技行銷、科技產業分析、創業管理，擇一
    const sim2 = new Node(courses.slice(8, 11), { max_children: 1 }, '擇一');

    // 非管理背景學生必修 (4選2)
    const tbl3 = new Node([courses[7], sim2, ...courses.slice(11, 13)], { min_children: 2 }, '非管理背景學生必修：至少 2 門');

    // 必修(至少9學分)
    const cat1 = new Node([tbl1, tbl2, tbl3], { min_credit: 9 }, '一、必修：至少 9 學分');

    // 選修
    const tbl4 = new Node(courses.slice(13), {}, '選修');
    const cat2 = new Node([tbl4], {}, '二、選修');

    // 生物科技管理學分學程
    const root = new Node([cat1, cat2], { min_credit: 15 });
    root.validate();
    if (this.test) console.log(root.validate(), root.credit);
    return root;
  }

  // 法律學分學程
  LAW() {
    const courses = this.courses['法律學分學程'];
    if (this.test) this.printEnrolled(courses);

    // 必修科目：至少取得3門必修科目學分
    const tbl1 = new Node(courses.slice(0, 5), { min_children: 3 }, '必修');

    const cat1 = new Node([tbl1], {}, '一、必修：至少 3 門課');

    // 選修
    // 選修：基礎法學領域
    const tbl2 = new Node(courses.slice(5, 18), {}, '基礎法學領域');

    // 選修：智慧財產法學領域
    const tbl3 = new Node(courses.slice(18, 24), {}, '智慧財產法學領域');

    // 選修：生醫科技法學領域
    const tbl4 = new Node(courses.slice(24, 30), {}, '生醫科技法學領域');

    // 選修：資訊通訊法律領域
    const tbl5 = new Node(courses.slice(30, 35), {}, '資訊通訊法律領域');

    // 選修：環境能源法學領域
    const tbl6 = new Node(courses.slice(35, 40), {}, '環境能源法學領域');

    // 選修：比較國際法學領域
    const tbl7 = new Node(courses.slice(40), {}, '比較國際法學領域');

    const cat2 = new Node([tbl2, tbl3, tbl4, tbl5, tbl6, tbl7], {}, '二、選修');

    // 法律學分學程
    const root = new Node([cat1, cat2], { min_credit: 15 });
    root.validate();
    if (this.test) console.log(root.validate(), root.credit);
    return root;
  }

  // 創新設計學分學程
  ID() {
    const courses = this.courses['創新設計學分學程'];
    if (this.test) this.printEnrolled(courses);

    // 核心課程(至少6學分)
    const tbl1 = new Node(courses.slice(0, 8), { min_credit: 6 }, '核心課程');
    const cat1 = new Node([tbl1], {}, '一、核心課程：至少 6 學分');

    // 以下選修
    const tbl2 = new Node(courses.slice(8), {}, '選修');
    const cat2 = new Node([tbl2], {}, '二、選修');

    // 創新設計學分學程
    const root = new Node([cat1, cat2], { min_credit: 15 });
    root.validate();
    if (this.test) console.log(root.validate(), root.credit);
    return root;
  }

  // 藝術學分學程
  ART() {
    const courses = this.courses['藝術學分學程'];
    if (this.test) this.printEnrolled(courses);

    // Ａ類：「藝術與文學」向度之藝術相關課程 (至少一門)
    const tbl1 = new Node(courses.slice(0, 12), { min_children: 1 }, 'A 類');

    // B類：操作型藝術課程 (至少一門)
    const tbl2 = new Node(courses.slice(12, 32), { min_children: 1 }, 'B 類');
    const cat1 = new Node([tbl1, tbl2], {}, '一、必修：兩類各至少 1 門課');

    // 以下選修
    const tbl3 = new Node(courses.slice(32, 80), {}, '歷史與理論');
    const tbl4 = new Node(courses.slice(80), {}, '文化政策與藝術管理');
    const cat2 = new Node([tbl3, tbl4], {}, '二、選修');

    // 藝術學分學程
    const root = new Node([cat1, cat2], { min_credit: 15 });
    root.validate();
    if (this.test) console.log(root.validate(), root.credit);
    return root;
  }

  // 創新創業學分學程
  IE() {
    const courses = this.courses['創新創業學分學程'];
    if (this.test) this.printEnrolled(courses);

    // 基礎課程 至少9學分（3類各3學分）
    const tbl1 = new Node(courses.slice(0, 5), { min_credit: 3 }, '創業實作類');
    const tbl2 = new Node(courses.slice(5, 17), { min_credit: 3 }, '服務/產品/系統設計類');
    const tbl3 = new Node(courses.slice(17, 34), { min_credit: 3 }, '創意創新類');
    const cat1 = new Node([tbl1, tbl2, tbl3], {}, '一、基礎課程：三類各 3 學分');

    // 支持性至少6學分（3類至少選2類各3學分)
    const tbl4 = new Node(courses.slice(34, 53), { min_credit: 3 }, '商管法律');
    const tbl5 = new Node(courses.slice(53, 59), { min_credit: 3 }, '科學/科技素養');
    const tbl6 = new Node(courses.slice(59), { min_credit: 3 }, '人文社會藝術素養');
    const cat2 = new Node([tbl4, tbl5, tbl6], { min_valid_children_node: 2 }, '二、支持性課程：三類選兩類各 3 學分')

    // 創新創業學分學程
    const root = new Node([cat1, cat2], { min_credit: 15 });
    root.validate();
    if (this.test) console.log(root.validate(), root.credit);
    return root;
  }

  // 教育知能學分學程
  EF() {
    const courses = this.courses['教育知能學分學程'];
    if (this.test) this.printEnrolled(courses);

    const tbl1 = new Node(courses.slice(0, 4), { min_children: 2 }, '共同基礎');
    const cat1 = new Node([tbl1], {}, '一、共同基礎：至少 2 門課');

    const tbl2 = new Node(courses.slice(4, 15), { min_children: 1 }, '認知與教學');
    const cat2 = new Node([tbl2], {}, '二、認知與教學：至少 1 門課');

    const tbl3 = new Node(courses.slice(15), { min_children: 1 }, '社會與人際');
    const cat3 = new Node([tbl3], {}, '三、社會與人際：至少 1 門課');

    // 教育知能學分學程
    const root = new Node([cat1, cat2, cat3], { min_credit: 16 });
    root.validate();
    if (this.test) console.log(root.validate(), root.credit);
    return root;
  }

  // 數據科學學分學程
  DS() {
    const courses = this.courses['數據科學學分學程'];
    if (this.test) this.printEnrolled(courses);

    const sim1 = new Node(courses.slice(0, 4), { max_children: 1 }, '擇一');
    const sim2 = new Node(courses.slice(4, 6), { max_children: 1 }, '擇一');
    const sim3 = new Node(courses.slice(6, 9), { max_children: 1 }, '擇一');
    const tbl1 = new Node([sim1, sim2, sim3, ...courses.slice(9, 13)], { min_credit: 9 }, '核心課程');
    const cat1 = new Node([tbl1], {}, '一、核心課程：至少 9 學分');

    const sim4 = new Node(courses.slice(28, 30), { max_children: 1 }, '擇一');
    const sim5 = new Node(courses.slice(30, 32), { max_children: 1 }, '擇一');
    const sim6 = new Node(courses.slice(32, 34), { max_children: 1 }, '擇一');
    const tbl2 = new Node([...courses.slice(13, 28), sim4, sim5, sim6, ...courses.slice(34)], {}, '選修課程');
    const cat2 = new Node([tbl2], {}, '二、選修課程');

    // 數據科學學分學程
    const root = new Node([cat1, cat2], { min_credit: 18 });
    root.validate();
    if (this.test) console.log(root.validate(), root.credit);
    return root;
  }

  // 永續發展與環境管理學分學程
  SDEM() {
    const courses = this.courses['永續發展與環境管理學分學程'];
    if (this.test) this.printEnrolled(courses);

    const tbl1 = new Node(courses, { min_credit: 15 }, '選修');
    const cat1 = new Node([tbl1], {}, '一、選修：至少 15 學分');

    // 永續發展與環境管理學分學程
    const root = new Node([cat1], { min_credit: 15 });
    root.validate();
    if (this.test) console.log(root.validate(), root.credit);
    return root;
  }

  // 觀光旅遊學分學程
  TT() {
    const courses = this.courses['觀光旅遊學分學程'];
    if (this.test) this.printEnrolled(courses);

    const tbl1 = new Node(courses.slice(0, 3), { min_children: 1 }, '核心課程');
    const cat1 = new Node([tbl1], {}, '一、核心課程：至少 1 門課');

    const tbl2 = new Node(courses.slice(3, 11), { min_children: 1 }, '一般類');
    const cat2 = new Node([tbl2], {}, '二、選修課程：至少 1 門課');

    const tbl3 = new Node(courses.slice(11), { min_children: 1 }, '語言類');
    const cat3 = new Node([tbl3], {}, '三、選修課程：至少 1 門課');

    // 觀光旅遊學分學程
    const root = new Node([cat1, cat2, cat3], { min_credit: 15 });
    root.validate();
    if (this.test) console.log(root.validate(), root.credit);
    return root;
  }
}

export default Program;